import * as React from 'react';
import { useTdsRegister, useMarkTdsFiled, type TdsRegisterItem, type TdsStatus } from '../network/tax.api.js';
import { DataTable } from '@repo/web-list';
import { defineListPage } from '@repo/list';
import { StatusBadge } from '../../../components/ui/StatusBadge.js';
import { AmountDisplay } from '../../../components/ui/AmountDisplay.js';
import { StatCard } from '../../../components/layout/StatCard.js';
import { RecordChallanModal } from './RecordChallanModal.js';
import { Button } from '../../../components/ui/Button.js';
import { useToast } from '../../../components/ui/Toast.js';

interface TdsRegisterProps {
 schemeId: string;
 onSchemeIdChange: (id: string) => void;
 schemes: any[];
 canUpdate?: boolean;
}

const STATUS_CONFIG: Record<TdsStatus, { label: string; variant: 'warning' | 'info' | 'success' | 'danger' }> = {
 PENDING: { label: 'Pending Challan', variant: 'warning' },
 CHALLAN_RECORDED: { label: 'Challan Recorded', variant: 'info' },
 FILED: { label: 'Filed', variant: 'success' },
};

export function TdsRegister({ schemeId, onSchemeIdChange, schemes, canUpdate = true }: TdsRegisterProps) {
 const [page, setPage] = React.useState(1);
 const limit = 20;
 const { toast } = useToast();

 const { data, isLoading, error, refetch } = useTdsRegister({
 schemeId: schemeId || undefined,
 page,
 limit,
 });

 const markFiled = useMarkTdsFiled();
 const [recordChallanId, setRecordChallanId] = React.useState<string | null>(null);

 const handleMarkFiled = async (id: string) => {
 try {
 await markFiled.mutateAsync(id);
 toast({ type: 'success', title: 'TDS Marked Filed', description: 'TDS entry status updated to Filed.' });
 } catch (err: unknown) {
 const msg = err instanceof Error ? err.message : 'Failed to mark TDS as filed';
 toast({ type: 'error', title: 'Error', description: msg });
 console.error(err);
 }
 };

 // Calculate local page aggregates for stats
 const items = data?.data ?? [];
 const totalTdsDeducted = items.reduce((sum, item) => sum + item.tdsAmount, 0);
 const pendingChallansCount = items.filter((item) => item.status === 'PENDING').length;
 const filedTdsAmount = items.filter((item) => item.status === 'FILED').reduce((sum, item) => sum + item.tdsAmount, 0);

  const config = defineListPage<TdsRegisterItem>({
  variant: 'data-table',
  resource: 'tds',
  columns: [
  { id: 'deducteeName', header: 'Deductee Name', accessor: 'deducteeName', renderType: 'text' },
  { id: 'deducteePan', header: 'PAN', accessor: 'deducteePan', renderType: 'code' },
  { id: 'grossAmount', header: 'Gross Amount', accessor: 'amountPaid', renderType: 'money', numeric: true },
  { id: 'tdsAmount', header: 'TDS (Paise)', accessor: 'tdsAmount', renderType: 'money', numeric: true },
  { id: 'deductionDate', header: 'Deduction Date', accessor: 'paymentDate', renderType: 'date' },
  { id: 'status', header: 'Status', accessor: 'status', renderType: 'text' },
  ],
  });

  return (
  <div className="flex flex-col gap-6">
  {/* KPI Stats Grid */}
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
  <StatCard
  label="Total TDS Deducted"
  value={<AmountDisplay paise={totalTdsDeducted} />}
  />
  <StatCard
  label="Pending Challans"
  value={String(pendingChallansCount)}
  />
  <StatCard
  label="Filed TDS (This Page)"
  value={<AmountDisplay paise={filedTdsAmount} />}
  />
  </div>

  {/* Main Data Table */}
  <DataTable
  config={config}
  data={items}
  total={data?.meta?.total ?? 0}
  page={page}
  limit={limit}
  toolbarActions={
    <>
      <select
        value={schemeId}
        onChange={(e) => {
          onSchemeIdChange(e.target.value);
          setPage(1);
        }}
        className="bg-surface-1 border border-border text-text-primary px-3 py-1.5 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-accent-500 font-semibold"
      >
        <option value="">All Schemes</option>
        {schemes.map((s) => (
          <option key={s.id} value={s.id}>
            {s.displayName || s.schemeNumber}
          </option>
        ))}
      </select>
      <Button
        variant="ghost"
        onClick={() => void refetch()}
        className="text-xs font-semibold text-accent-500 hover:text-accent-600 transition-colors bg-transparent hover:bg-transparent"
      >
        Refresh
      </Button>
    </>
  }
  onPageChange={setPage}
  loading={isLoading}
  error={error ? 'Failed to load TDS register.' : undefined}
  onRetry={refetch}
        customRenderers={{
  status: (row: TdsRegisterItem) => {
  const s = row.status;
  const cfg = STATUS_CONFIG[s] ?? { label: s, variant: 'info' as const };
  return <StatusBadge status={cfg.variant} label={cfg.label} />;
  },
  challanInfo: (row: TdsRegisterItem) => {
  if (row.tdsChallanNo) {
  return (
  <div className="flex flex-col gap-0.5 text-xs text-text-secondary">
  <span className="font-mono font-medium">No: {row.tdsChallanNo}</span>
  {row.challanDate && (
  <span className="text-caption text-text-muted">
  Date: {new Date(row.challanDate).toLocaleDateString()}
  </span>
  )}
  </div>
  );
  }
  return <span className="text-text-muted text-xs">—</span>;
  },
  actions: (row: TdsRegisterItem) => {
  if (!canUpdate) {
  return <span className="text-xs text-text-muted">Read Only</span>;
  }
  if (row.status === 'PENDING') {
  return (
  <Button
  size="sm"
  variant="primary"
  onClick={(e) => {
  e.stopPropagation();
  setRecordChallanId(row.id);
  }}
  >
  Record Challan
  </Button>
  );
  }
  if (row.status === 'CHALLAN_RECORDED') {
  return (
  <Button
  size="sm"
  variant="secondary"
  loading={markFiled.isPending}
  onClick={(e) => {
  e.stopPropagation();
  void handleMarkFiled(row.id);
  }}
  >
  Mark Filed
  </Button>
  );
  }
  return <span className="text-xs font-medium text-success-text">Complete</span>;
  },
  }}
  />

 {/* Challan Modal */}
 <RecordChallanModal
 id={recordChallanId}
 isOpen={recordChallanId !== null}
 onClose={() => setRecordChallanId(null)}
 />
 </div>
 );
}
